import avisoLegalRaw from '../../../../../docs/legal/aviso-legal.md?raw';
import condicionesOrganizadorRaw from '../../../../../docs/legal/condiciones-del-organizador.md?raw';
import politicaCookiesRaw from '../../../../../docs/legal/politica-de-cookies.md?raw';
import politicaPrivacidadRaw from '../../../../../docs/legal/politica-de-privacidad.md?raw';
import terminosCondicionesRaw from '../../../../../docs/legal/terminos-y-condiciones.md?raw';

/**
 * Los cinco documentos legales (P-01, respuestas del cliente 2026-09-17): fuente única en
 * `docs/legal/*.md` (`docs/legal/00-mapa-legal.md` § 6, «Dónde vive cada documento»). Se importan
 * con `?raw` (tipado por `vite/client.d.ts`, `declare module '*?raw'`) para no duplicar el texto
 * legal en ningún otro sitio del repositorio — copiarlo aquí crearía una segunda fuente de verdad
 * que podría desincronizarse del original. `docs/legal/**` está fuera del ámbito de esta tarea
 * (no se edita), solo se lee.
 *
 * Cada `slug` es el nombre de fichero sin extensión, y dobla como segmento de
 * `/legal/$documento`. `claveI18n` es la clave dentro de `legal:documentos.*` (namespace ya
 * existente y congelado, ver `i18n/es/legal.json`): sirve el título y la descripción, en el
 * idioma de la interfaz, para el índice de `/legal` y para el pie de página — el CUERPO del
 * documento (importado arriba) se sirve siempre en español, nunca traducido (ver el JSDoc de
 * `paginaLegal.tsx`).
 */
export const DOCUMENTOS_LEGALES_SLUGS = [
	'aviso-legal',
	'terminos-y-condiciones',
	'politica-de-privacidad',
	'politica-de-cookies',
	'condiciones-del-organizador',
] as const;

export type DocumentoLegalSlug = (typeof DOCUMENTOS_LEGALES_SLUGS)[number];

/** Las cinco claves de `legal:documentos.*` (`i18n/{es,en}/legal.json`), una por `slug`. */
type ClaveI18nDocumentoLegal =
	| 'avisoLegal'
	| 'condicionesOrganizador'
	| 'politicaCookies'
	| 'politicaPrivacidad'
	| 'terminosCondiciones';

export interface DocumentoLegal {
	claveI18n: ClaveI18nDocumentoLegal;
	raw: string;
	slug: DocumentoLegalSlug;
}

/** Metadatos del frontmatter YAML de cada documento (líneas `clave: valor` entre `---`). Ninguno
 * de los cinco documentos usa YAML más allá de pares plano `clave: valor` (listas, anidamiento o
 * comillas simples no aparecen — solo `version` va entre comillas dobles), así que el parseo no
 * necesita un analizador YAML completo. */
export interface FrontmatterLegal {
	actualizado: string;
	sustituye?: string;
	titulo: string;
	url: string;
	version: string;
}

export interface DocumentoLegalParseado {
	/** El cuerpo Markdown, sin el bloque de frontmatter. Texto legal tal cual — no se reescribe. */
	contenido: string;
	frontmatter: FrontmatterLegal;
}

const REGISTRO: Record<DocumentoLegalSlug, DocumentoLegal> = {
	'aviso-legal': { claveI18n: 'avisoLegal', raw: avisoLegalRaw, slug: 'aviso-legal' },
	'condiciones-del-organizador': {
		claveI18n: 'condicionesOrganizador',
		raw: condicionesOrganizadorRaw,
		slug: 'condiciones-del-organizador',
	},
	'politica-de-cookies': { claveI18n: 'politicaCookies', raw: politicaCookiesRaw, slug: 'politica-de-cookies' },
	'politica-de-privacidad': {
		claveI18n: 'politicaPrivacidad',
		raw: politicaPrivacidadRaw,
		slug: 'politica-de-privacidad',
	},
	'terminos-y-condiciones': {
		claveI18n: 'terminosCondiciones',
		raw: terminosCondicionesRaw,
		slug: 'terminos-y-condiciones',
	},
};

/** Orden de lectura (`docs/legal/00-mapa-legal.md` § 6): quiénes somos → qué se contrata → qué
 * datos tratamos → qué cookies usamos → qué acepta quien organiza. Mismo orden en el índice de
 * `/legal` y en `PieLegal`, para no tener dos listas que puedan desincronizarse. */
export const DOCUMENTOS_LEGALES: DocumentoLegal[] = DOCUMENTOS_LEGALES_SLUGS.map(slug => REGISTRO[slug]);

export const esSlugDocumentoLegal = (valor: string): valor is DocumentoLegalSlug =>
	(DOCUMENTOS_LEGALES_SLUGS as readonly string[]).includes(valor);

const RE_FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** Quita las comillas dobles que envuelven un valor (`version: "2.0"` → `2.0`); el resto de
 * valores del frontmatter no las lleva. */
const limpiarValorFrontmatter = (valor: string): string => {
	const recortado = valor.trim();
	return recortado.startsWith('"') && recortado.endsWith('"') ? recortado.slice(1, -1) : recortado;
};

/**
 * Separa el frontmatter YAML (entre `---`) del cuerpo Markdown. Parseo mínimo línea a línea
 * (`clave: valor`), suficiente para el frontmatter real de los cinco documentos (ver JSDoc de
 * `FrontmatterLegal`) — no es un analizador YAML general, y no hace falta uno: instalar una
 * librería para esto sería una dependencia nueva sin verificar contra el registro npm (prohibido
 * fuera de una tarea de fundaciones).
 */
export const parsearDocumentoLegal = (raw: string): DocumentoLegalParseado => {
	const coincidencia = raw.match(RE_FRONTMATTER);
	if (!coincidencia) {
		throw new Error('Documento legal sin frontmatter');
	}

	const bloque = coincidencia[1] ?? '';
	const frontmatter: Record<string, string> = {};
	for (const linea of bloque.split('\n')) {
		const separador = linea.indexOf(':');
		if (separador === -1) {
			continue;
		}
		frontmatter[linea.slice(0, separador).trim()] = limpiarValorFrontmatter(linea.slice(separador + 1));
	}

	return {
		contenido: raw.slice(coincidencia[0].length),
		frontmatter: frontmatter as unknown as FrontmatterLegal,
	};
};

/** Los cinco documentos ya parseados (metadatos + cuerpo), calculado una sola vez al cargar el
 * módulo — los cinco `raw` son cadenas estáticas del propio bundle, así que parsearlos aquí en
 * vez de en cada render no cambia el resultado, solo evita repetir el trabajo. Consumido por el
 * índice de `/legal` (necesita `frontmatter.actualizado` de los cinco a la vez) y por
 * `obtenerDocumentoLegal`. */
export const DOCUMENTOS_LEGALES_PARSEADOS: Array<DocumentoLegal & DocumentoLegalParseado> = DOCUMENTOS_LEGALES.map(
	documento => ({ ...documento, ...parsearDocumentoLegal(documento.raw) }),
);

/** Documento parseado por `slug`, o `undefined` si el segmento de la URL no es uno de los cinco. */
export const obtenerDocumentoLegal = (slug: string): (DocumentoLegal & DocumentoLegalParseado) | undefined =>
	DOCUMENTOS_LEGALES_PARSEADOS.find(documento => documento.slug === slug);
